import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, Button, Space, Card, Divider, Row, Col, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { format, startOfYear, endOfYear, startOfMonth, endOfMonth, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import locale from 'antd/es/date-picker/locale/pt_BR';

// Configurar dayjs para português
dayjs.locale('pt-br');

const { Option } = Select;
const { RangePicker } = DatePicker;

const dateFieldOptions = [
  { label: 'Criado em', value: 'createdAt' },
  { label: 'Última venda', value: 'lastSale' },
  { label: 'Data de nascimento', value: 'birthDate' }
];

const filterTypeOptions = [
  { label: 'Intervalo de datas', value: 'range' },
  { label: 'Dia específico', value: 'specific_date' },
  { label: 'Mês específico', value: 'specific_month' },
  { label: 'Ano específico', value: 'specific_year' }
];

const monthOptions = [
  { label: 'Janeiro', value: 1 },
  { label: 'Fevereiro', value: 2 },
  { label: 'Março', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Maio', value: 5 },
  { label: 'Junho', value: 6 },
  { label: 'Julho', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Setembro', value: 9 },
  { label: 'Outubro', value: 10 },
  { label: 'Novembro', value: 11 },
  { label: 'Dezembro', value: 12 }
];

const CustomerDateFilterModal = ({ open, onCancel, onApply, initialFilters = [] }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState([]);
  const [formValues, setFormValues] = useState({});

  const addFilter = () => {
    setFilters(prevFilters => {
      const newFilter = {
        id: Date.now(),
        field: 'createdAt',
        type: 'range'
      };
      const newFilters = [...prevFilters, newFilter];

      // Inicializar valores padrão no form
      const newIndex = prevFilters.length;
      const defaultValues = {
        [`field_${newIndex}`]: 'createdAt',
        [`type_${newIndex}`]: 'range'
      };
      form.setFieldsValue(defaultValues);
      setFormValues(prev => ({ ...prev, ...defaultValues }));

      return newFilters;
    });
  };

  useEffect(() => {
    if (open) {
      if (initialFilters.length > 0) {
        // Converter os filtros iniciais para o formato interno e adicionar IDs
        const convertedFilters = initialFilters.map((filter, index) => ({
          id: filter.id || Date.now() + index,
          field: filter.field,
          type: filter.filterType || filter.type // Suporta ambos filterType e type
        }));
        
        setFilters(convertedFilters);
        
        // Preencher o form com os filtros iniciais
        const initialValues = {};
        initialFilters.forEach((filter, index) => {
          const filterType = filter.filterType || filter.type;
          
          initialValues[`field_${index}`] = filter.field;
          initialValues[`type_${index}`] = filterType;

          if (filterType === 'range' && filter.startDate && filter.endDate) {
            // Antd DatePicker precisa de objetos dayjs válidos
            initialValues[`range_${index}`] = [dayjs(filter.startDate), dayjs(filter.endDate)];
          } else if (filterType === 'specific_date' && filter.startDate) {
            initialValues[`date_${index}`] = dayjs(filter.startDate);
          } else if (filterType === 'specific_month') {
            if (filter.month) {
              initialValues[`month_${index}`] = filter.month;
            }
            if (filter.year) {
              initialValues[`year_for_month_${index}`] = dayjs().year(filter.year);
            }
          } else if (filterType === 'specific_year' && filter.year) {
            initialValues[`year_${index}`] = dayjs().year(filter.year);
          }
        });
        
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
      } else {
        addFilter();
      }
    } else {
      setFilters([]);
      form.resetFields();
      setFormValues({});
    }
  }, [open, initialFilters]);

  const removeFilter = (filterId) => {
    const filterIndex = filters.findIndex(f => f.id === filterId);
    const newFilters = filters.filter(f => f.id !== filterId);
    setFilters(newFilters);

    // Reorganizar campos do form após remoção
    const currentValues = form.getFieldsValue();
    const newValues = {};

    newFilters.forEach((filter, newIndex) => {
      const oldIndex = filters.findIndex(f => f.id === filter.id);
      if (oldIndex !== -1 && oldIndex !== newIndex) {
        // Mover valores para o novo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            const newKey = key.replace(`_${oldIndex}`, `_${newIndex}`);
            newValues[newKey] = currentValues[key];
          }
        });
      } else if (oldIndex === newIndex) {
        // Manter valores no mesmo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            newValues[key] = currentValues[key];
          }
        });
      }
    });

    form.setFieldsValue(newValues);
    setFormValues(newValues);
  };

  const handleApply = () => {
    form.validateFields().then(values => {
      try {
        const processedFilters = filters.map((filter, index) => {
          const field = values[`field_${index}`];
          const type = values[`type_${index}`];

          let processedFilter = { field, filterType: type };

          switch (type) {
            case 'range':
              const range = values[`range_${index}`];
              if (range && range.length === 2) {
                // Converter dayjs para Date antes de usar date-fns
                const startDate = range[0].toDate ? range[0].toDate() : range[0];
                const endDate = range[1].toDate ? range[1].toDate() : range[1];
                processedFilter.startDate = format(startDate, 'yyyy-MM-dd');
                processedFilter.endDate = format(endDate, 'yyyy-MM-dd');
              }
              break;
            case 'specific_date':
              const date = values[`date_${index}`];
              if (date) {
                // Converter dayjs para Date antes de usar date-fns
                const dateObj = date.toDate ? date.toDate() : date;
                processedFilter.startDate = format(dateObj, 'yyyy-MM-dd');
                processedFilter.endDate = format(dateObj, 'yyyy-MM-dd');
              }
              break;
            case 'specific_month':
              const month = values[`month_${index}`];
              const yearForMonth = values[`year_for_month_${index}`];
              if (month) {
                processedFilter.month = month;
                if (yearForMonth) {
                  // Se tem ano, criar range específico
                  let yearNumber;
                  if (typeof yearForMonth === 'object' && yearForMonth !== null) {
                    if (yearForMonth.getFullYear) {
                      // É um objeto Date nativo
                      yearNumber = yearForMonth.getFullYear();
                    } else if (yearForMonth.year && typeof yearForMonth.year === 'function') {
                      // É um objeto Day.js/Moment (Antd DatePicker)
                      yearNumber = yearForMonth.year();
                    } else if (yearForMonth.$y !== undefined) {
                      // É um objeto Day.js - propriedade direta
                      yearNumber = yearForMonth.$y;
                    } else {
                      console.error('Tipo de objeto de ano desconhecido:', yearForMonth);
                      return;
                    }
                  } else if (typeof yearForMonth === 'number') {
                    yearNumber = yearForMonth;
                  } else if (typeof yearForMonth === 'string') {
                    yearNumber = parseInt(yearForMonth, 10);
                  } else {
                    console.error('Tipo de ano inválido:', yearForMonth);
                    return;
                  }

                  processedFilter.year = yearNumber;
                  const startDate = startOfMonth(new Date(yearNumber, month - 1));
                  const endDate = endOfMonth(new Date(yearNumber, month - 1));
                  processedFilter.startDate = format(startDate, 'yyyy-MM-dd');
                  processedFilter.endDate = format(endDate, 'yyyy-MM-dd');
                }
                // Se não tem ano, não precisa definir startDate/endDate
                // O backend vai filtrar apenas pelo mês usando EXTRACT
              }
              break;
            case 'specific_year':
              const year = values[`year_${index}`];
              if (year) {
                let yearNumber;
                if (typeof year === 'object' && year !== null) {
                  if (year.getFullYear) {
                    // É um objeto Date nativo
                    yearNumber = year.getFullYear();
                  } else if (year.year && typeof year.year === 'function') {
                    // É um objeto Day.js/Moment (Antd DatePicker)
                    yearNumber = year.year();
                  } else if (year.$y !== undefined) {
                    // É um objeto Day.js - propriedade direta
                    yearNumber = year.$y;
                  } else {
                    console.error('Tipo de objeto de ano desconhecido:', year);
                    break;
                  }
                } else if (typeof year === 'number') {
                  // É um número
                  yearNumber = year;
                } else if (typeof year === 'string') {
                  // É uma string, converter para número
                  yearNumber = parseInt(year, 10);
                } else {
                  console.error('Tipo de ano inválido:', year);
                  break;
                }

                processedFilter.year = yearNumber;
                // Para ano específico, não precisamos de startDate/endDate
                // O backend vai usar EXTRACT(year FROM campo) = yearNumber
              }
              break;
          }

          return processedFilter;
        }).filter(filter => {
          // Remover filtros incompletos
          switch (filter.filterType) {
            case 'range':
              return filter.startDate && filter.endDate;
            case 'specific_date':
              return filter.startDate; // specific_date usa startDate
            case 'specific_month':
              return filter.month; // month é obrigatório, year é opcional
            case 'specific_year':
              return filter.year;
            default:
              return false;
          }
        });

        onApply(processedFilters);
      } catch (error) {
        console.error('Error processing filters:', error);
      }
    }).catch(errorInfo => {
      console.error('Validation Failed:', errorInfo);
    });
  };

  const renderFilterInput = (filter, index) => {
    const type = formValues[`type_${index}`] || 'range';

    switch (type) {
      case 'range':
        return (
          <Form.Item
            name={`range_${index}`}
            label="Período"
            rules={[{ required: true, message: 'Selecione o período' }]}
          >
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Data inicial', 'Data final']}
              locale={locale}
            />
          </Form.Item>
        );
      case 'specific_date':
        return (
          <Form.Item
            name={`date_${index}`}
            label="Data"
            rules={[{ required: true, message: 'Selecione a data' }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="Selecione a data"
              locale={locale}
            />
          </Form.Item>
        );
      case 'specific_month':
        return (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={`month_${index}`}
                label="Mês"
                rules={[{ required: true, message: 'Selecione o mês' }]}
              >
                <Select placeholder="Selecione o mês">
                  {monthOptions.map(month => (
                    <Option key={month.value} value={month.value}>
                      {month.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={`year_for_month_${index}`}
                label="Ano (opcional)"
              >
                <DatePicker
                  picker="year"
                  placeholder="Ano"
                  format="YYYY"
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
        );
      case 'specific_year':
        return (
          <Form.Item
            name={`year_${index}`}
            label="Ano"
            rules={[{ required: true, message: 'Selecione o ano' }]}
          >
            <DatePicker
              picker="year"
              placeholder="Selecione o ano"
              format="YYYY"
              locale={locale}
            />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  const onFormValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
  };

  const getFilterDescription = (filter, index) => {
    const field = formValues[`field_${index}`] || 'createdAt';
    const type = formValues[`type_${index}`] || 'range';

    const fieldLabel = dateFieldOptions.find(opt => opt.value === field)?.label || field;
    const typeLabel = filterTypeOptions.find(opt => opt.value === type)?.label || type;

    return `${fieldLabel} - ${typeLabel}`;
  };

  return (
    <Modal
      title="Filtros Avançados por Data"
      open={open}
      onCancel={onCancel}
      translate="no"
      className="notranslate"
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="clear" onClick={() => {
          setFilters([]);
          form.resetFields();
          onApply([]);
        }}>
          Limpar Filtros
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Aplicar Filtros
        </Button>
      ]}
      width={800}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
        {filters.map((filter, index) => (
          <Card
            key={filter.id}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <span>Filtro {index + 1}</span>
                <Tag color="blue">{getFilterDescription(filter, index)}</Tag>
              </Space>
            }
            extra={
              filters.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilter(filter.id)}
                >
                  Remover
                </Button>
              )
            }
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={`field_${index}`}
                  label="Campo de Data"
                  initialValue="createdAt"
                  rules={[{ required: true, message: 'Selecione o campo' }]}
                >
                  <Select placeholder="Selecione o campo">
                    {dateFieldOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={`type_${index}`}
                  label="Tipo de Filtro"
                  initialValue="range"
                  rules={[{ required: true, message: 'Selecione o tipo' }]}
                >
                  <Select placeholder="Selecione o tipo">
                    {filterTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {renderFilterInput(filter, index)}
          </Card>
        ))}

        <Button
          type="dashed"
          onClick={addFilter}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 16 }}
        >
          Adicionar Filtro
        </Button>
      </Form>
    </Modal>
  );
};

export default CustomerDateFilterModal;